// R1b-γ — Tabbed Operations shell. Same pattern as PeopleRolesShell + FinancialShell.
// URL is source of truth: /admin/operations/{projects|documents|compliance}.
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HardHat, FolderOpen, ShieldCheck } from "lucide-react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import OperationsProjectsTab from "./tabs/OperationsProjectsTab";
import OperationsDocumentsTab from "./tabs/OperationsDocumentsTab";
import OperationsComplianceTab from "./tabs/OperationsComplianceTab";

const TABS = [
  { key: "projects",   label: "Projects",   icon: HardHat,     Body: OperationsProjectsTab,   testid: "operations-tab-projects" },
  { key: "documents",  label: "Documents",  icon: FolderOpen,  Body: OperationsDocumentsTab,  testid: "operations-tab-documents" },
  { key: "compliance", label: "Compliance", icon: ShieldCheck, Body: OperationsComplianceTab, testid: "operations-tab-compliance" },
];

export default function OperationsShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tab: tabParam } = useParams();
  const current = TABS.find((t) => t.key === tabParam) || TABS[0];
  const CurrentBody = current.Body;

  return (
    <AdminAppShell
      activeNav="operations"
      title="Operations"
      subtitle="Projects · Documents · Compliance"
      breadcrumbs={[
        { label: "Owner Command Center", to: "/owner" },
        { label: "Operations", to: "/admin/operations/projects" },
        { label: current.label, to: null },
      ]}
      user={user}
    >
      <div
        role="tablist"
        aria-label="Operations sections"
        data-testid="operations-tablist"
        style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid #E8E4D9", overflowX: "auto" }}
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.key === current.key;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`operations-panel-${t.key}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={t.testid}
              onClick={() => navigate(`/admin/operations/${t.key}`)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  const dir = e.key === "ArrowRight" ? 1 : -1;
                  const idx = TABS.findIndex((x) => x.key === current.key);
                  const next = TABS[(idx + dir + TABS.length) % TABS.length];
                  navigate(`/admin/operations/${next.key}`);
                }
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 16px", minHeight: 44,
                background: isActive ? "#FFFFFF" : "transparent",
                border: 0, borderBottom: isActive ? "2px solid #B8892B" : "2px solid transparent",
                marginBottom: "-1px",
                fontFamily: '"Inter Tight", system-ui, sans-serif',
                fontSize: 13, fontWeight: isActive ? 600 : 500,
                color: isActive ? "#0F0F10" : "#4A4A4C",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              <Icon size={14} aria-hidden="true" /> {t.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={`operations-panel-${current.key}`} data-testid={`operations-panel-${current.key}`}>
        <CurrentBody />
      </div>
    </AdminAppShell>
  );
}
